// Hash-based router (~no deps). Hash routing means the server only ever serves
// index.html regardless of route, so deep links never 404 — path-agnostic, the
// same app serves the root and the /poc/ staging slot unchanged.
import { h, type Child } from './dom';
import { nextScope } from './signal';

export interface Route {
	pattern: RegExp;
	render: (params: Record<string, string>, query: URLSearchParams) => Child;
}

export function route(path: string, render: Route['render']): Route {
	// "/debts/:id" -> named-group regex.
	const pattern = new RegExp(
		'^' + path.replace(/:[^/]+/g, (m) => `(?<${m.slice(1)}>[^/]+)`) + '$'
	);
	return { pattern, render };
}

// In-app navigation guard (e.g. unsaved budget edits). A route registers it
// during render; it is consulted before the next route render and cleared
// after any allowed navigation. Return false to cancel (the hash is restored).
let navGuard: (() => boolean) | null = null;
export function setNavGuard(g: (() => boolean) | null): void {
	navGuard = g;
}

export function startRouter(outlet: Element, routes: Route[]): void {
	let currentHash = location.hash;
	let restoring = false;
	const handle = () => {
		// A cancelled navigation restores the previous hash, which re-fires
		// hashchange — skip that echo instead of re-rendering.
		if (restoring) {
			restoring = false;
			return;
		}
		if (navGuard) {
			if (!navGuard()) {
				restoring = true;
				location.hash = currentHash.slice(1) || '/';
				return;
			}
			navGuard = null;
		}
		currentHash = location.hash;
		// Rotate the effect scope: dispose the previous route's effects before
		// rendering the next view (see signal.ts — async stragglers are reclaimed
		// one navigation later).
		const disposePreviousRoute = nextScope();
		disposePreviousRoute();
		// Query params ride inside the hash (#/transactions?month=...&category=...)
		// so deep links survive CloudFront's single index.html.
		const hash = location.hash.slice(1) || '/';
		const qIdx = hash.indexOf('?');
		const path = qIdx === -1 ? hash : hash.slice(0, qIdx);
		const query = new URLSearchParams(qIdx === -1 ? '' : hash.slice(qIdx + 1));
		for (const r of routes) {
			const m = r.pattern.exec(path);
			if (m) {
				const out = r.render(m.groups ?? {}, query);
				outlet.replaceChildren(
					...(Array.isArray(out) ? out : [out])
						.flat(Infinity as 1)
						.filter((c): c is Node | string => c != null && c !== false)
						.map((c) => (c instanceof Node ? c : document.createTextNode(String(c))))
				);
				// New view starts at the top (jsdom in the smoke stubs scrollTo).
				try {
					scrollTo(0, 0);
				} catch {
					/* non-browser environments */
				}
				return;
			}
		}
		outlet.replaceChildren(h('div', { class: 'state-error' }, 'Page not found'));
	};
	addEventListener('hashchange', handle);
	handle();
}

export function navigate(path: string): void {
	location.hash = path;
}
